import type { ComponentType, SVGProps } from 'react';
import { Link } from 'react-router-dom';
import { useCrashBounce } from '../hooks/useCrashBounce';
import { useBreakoutWatch } from '../hooks/useBreakoutWatch';
import { useListingRadar } from '../hooks/useListingRadar';
import { useMakerForward } from '../hooks/useMakerForward';
import { useLiquidityRewards } from '../hooks/useLiquidityRewards';
import { useMartingale } from '../hooks/useMartingale';
import { useBtc15PriceRules } from '../hooks/useBtc15PriceRules';
import { useRiptideRule } from '../hooks/useRiptideRule';
import { useEthHourlyRule } from '../hooks/useEthHourlyRule';
import { useHourlyPooledRule } from '../hooks/useHourlyPooledRule';
import { useBenchmark } from '../hooks/useBenchmark';
import { Disclaimer } from '../components/Disclaimer';
import {
  BreakoutIcon,
  CrashIcon,
  DoublingIcon,
  RadarIcon,
  RewardIcon,
  SpreadIcon,
  TimerIcon,
} from '../components/icons';
import { formatSignedUsd } from '../lib/format';
import type {
  BenchmarkResponse,
  LiquidityRewardsResponse,
  MartingaleResponse,
} from '../lib/apiClient';
import {
  FROM_PAPER_TESTS,
  paperTestSummary,
  type PaperTestPath,
} from '../lib/paperTestPages';
import styles from './PaperTestsPage.module.css';

type Verdict = 'collecting' | 'passing' | 'failing';

const VERDICT_LABEL: Record<Verdict, string> = {
  collecting: 'Collecting',
  passing: 'Passing',
  failing: 'Failing',
};

/** Decided tests first: they are the news. */
const VERDICT_ORDER: Record<Verdict, number> = { passing: 0, failing: 1, collecting: 2 };

const signedPct = (x: number | null) =>
  x === null ? '—' : `${x > 0 ? '+' : x < 0 ? '−' : ''}${Math.abs(x * 100).toFixed(1)}%`;

const tone = (x: number | null) =>
  x === null || Math.abs(x) < 1e-9
    ? styles.muted
    : x > 0
      ? styles.positive
      : styles.negative;

interface Row {
  to: PaperTestPath | '/btc-15m-predictor?tab=rules';
  name: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  what: string;
  /** Null while loading or unavailable. */
  verdict: Verdict | null;
  /** Shown in place of the verdict badge, e.g. "4 failing". */
  verdictText?: string;
  progress: string;
  /** Share of the way to the pass bar, 0–1; drawn as a bar while collecting. */
  progressFraction?: number;
  headline: string;
  headlineValue: number | null;
}

/** What a query gives a row while it has no data. */
function pending(
  isError: boolean,
): Pick<Row, 'verdict' | 'progress' | 'headline' | 'headlineValue'> {
  return {
    verdict: null,
    progress: isError ? 'Couldn’t load this test right now.' : 'Loading…',
    headline: '',
    headlineValue: null,
  };
}

/** Two paper bankrolls, one card: the better verdict leads, busts and combined P&L shown. */
function martingaleRow(
  sides: MartingaleResponse['sides'][number]['summary'][],
): Pick<Row, 'verdict' | 'progress' | 'headline' | 'headlineValue'> {
  const verdict: Verdict = sides.some((s) => s.verdict === 'passing')
    ? 'passing'
    : sides.some((s) => s.verdict === 'failing')
      ? 'failing'
      : 'collecting';
  const bets = sides.reduce((n, s) => n + s.bets, 0);
  const pnl = sides.reduce((n, s) => n + s.pnl, 0);
  return {
    verdict,
    progress: sides
      .map(
        (s) => `${s.side.toUpperCase()} ${s.busts} ${s.busts === 1 ? 'bust' : 'busts'}`,
      )
      .join(' · '),
    headline: bets ? `${formatSignedUsd(pnl)} after top-ups` : 'No rounds yet',
    headlineValue: bets ? pnl : null,
  };
}

/** Two buckets, one card: the better verdict leads, both nets shown. */
function liquidityRow(
  buckets: LiquidityRewardsResponse['buckets'],
): Pick<Row, 'verdict' | 'verdictText' | 'progress' | 'headline' | 'headlineValue'> {
  const verdict: Verdict = buckets.some((b) => b.verdict === 'passing')
    ? 'passing'
    : buckets.some((b) => b.verdict === 'failing')
      ? 'failing'
      : 'collecting';
  const programs = buckets.reduce((s, b) => s + b.programs, 0);
  const net = buckets.reduce((s, b) => s + b.pessimisticNet, 0);
  return {
    verdict,
    progress: `Programs: ${buckets.map((b) => `${b.programs} ${b.bucket}`).join(', ')}`,
    headline: programs ? `${formatSignedUsd(net)} pessimistic` : 'No programs yet',
    headlineValue: programs ? net : null,
  };
}

function PaperTestCard({ row }: { row: Row }) {
  return (
    <li>
      <Link
        to={row.to}
        // The rules live on the predictor; this lets that page link back here.
        state={row.to.startsWith('/btc-15m-predictor') ? FROM_PAPER_TESTS : undefined}
        className={styles.card}
      >
        <span className={styles.cardHead}>
          <span className={styles.name}>
            <row.Icon className={styles.icon} width={20} height={20} />
            {row.name}
          </span>
          {row.verdict && (
            <span className={`${styles.verdict} ${styles[row.verdict]}`}>
              {row.verdictText ?? VERDICT_LABEL[row.verdict]}
            </span>
          )}
        </span>
        <span className={styles.what}>{row.what}</span>
        {row.verdict === 'collecting' && row.progressFraction !== undefined && (
          <span className={styles.bar} aria-hidden="true">
            <span
              className={styles.barFill}
              style={{
                width: `${(Math.min(1, row.progressFraction) * 100).toFixed(1)}%`,
              }}
            />
          </span>
        )}
        <span className={styles.numbers}>
          <span>{row.progress}</span>
          {row.headline && (
            <span className={`${styles.headline} ${tone(row.headlineValue)}`}>
              {row.headline}
            </span>
          )}
        </span>
      </Link>
    </li>
  );
}

/**
 * Every forward paper test on one screen: one card each with its verdict,
 * how far it is toward its pass bar, and its headline result, linking to
 * the test's own page. Decided tests are listed first. Paper only.
 */
const usd0 = (x: number) =>
  `$${x.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const benchDay = (iso: string) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });

/**
 * The 60/40 index portfolio every test is measured against. Not a test: it
 * has no verdict, so it sits above the list instead of in it.
 */
function BenchmarkCard({ data }: { data: BenchmarkResponse | undefined }) {
  const r = data?.result ?? null;
  const bt = data?.backtest;
  return (
    <section className={styles.benchmark} aria-labelledby="benchmark-heading">
      <div className={styles.cardHead}>
        <h2 id="benchmark-heading" className={styles.benchmarkTitle}>
          The bar to beat
        </h2>
        <span className={`${styles.verdict} ${styles.collecting}`}>Benchmark</span>
      </div>
      <p className={styles.numbers}>
        <span>60/40 index portfolio</span>
        {r ? (
          <span className={`${styles.headline} ${tone(r.totalReturn)}`}>
            {usd0(r.startValue)} → {usd0(r.value)} ({signedPct(r.totalReturn)})
          </span>
        ) : (
          <span className={styles.muted}>
            {data ? `Starts at the ${benchDay(data.rule.startDate)} close` : 'Loading…'}
          </span>
        )}
      </p>
      <p className={styles.what}>
        60% S&amp;P 500 (SPY), 40% 7–10 year Treasuries (IEF), dividends included,
        rebalanced monthly.
        {r &&
          ` Since ${benchDay(r.startDate)}, through ${benchDay(r.asOfDate)}; worst dip ${signedPct(r.maxDrawdown)}.`}
        {bt &&
          ` Backtest ${bt.period}: ${(bt.cagr * 100).toFixed(1)}% a year, worst drawdown ${signedPct(bt.maxDrawdown)}.`}{' '}
        A test that can&apos;t beat this isn&apos;t worth the risk.
      </p>
      {r?.priceOnly && (
        <p className={styles.what}>
          Today&apos;s prices came from the backup source, which leaves dividends out, so
          this reads a little low.
        </p>
      )}
    </section>
  );
}

export function PaperTestsPage() {
  const crash = useCrashBounce();
  const breakout = useBreakoutWatch();
  const listing = useListingRadar();
  const maker = useMakerForward();
  const rewards = useLiquidityRewards();
  const martingale = useMartingale();
  const priceRules = useBtc15PriceRules();
  const riptide = useRiptideRule();
  const ethHourly = useEthHourlyRule();
  const pooled = useHourlyPooledRule();
  const benchmark = useBenchmark();

  // The predictor's locked rules, summed up as one card.
  const ruleScores = [
    ...(priceRules.data?.summary ?? []),
    ...(riptide.data ? [riptide.data.summary] : []),
    ...(ethHourly.data ? [ethHourly.data.summary] : []),
    ...(pooled.data ? [pooled.data.summary] : []),
  ];
  const ruleCounts = { passing: 0, failing: 0, collecting: 0 };
  for (const score of ruleScores) ruleCounts[score.verdict]++;
  const rulesVerdict: Verdict | null = ruleScores.length
    ? ruleCounts.passing
      ? 'passing'
      : ruleCounts.failing
        ? 'failing'
        : 'collecting'
    : null;

  const rows: Row[] = [
    {
      to: '/btc-15m-predictor?tab=rules',
      name: '15m price rules',
      Icon: TimerIcon,
      what: 'Locked rules for Kalshi’s BTC 15-minute and hourly markets, scored per contract after fees.',
      ...(ruleScores.length
        ? {
            verdict: rulesVerdict,
            verdictText:
              ruleCounts.passing || ruleCounts.failing
                ? [
                    ruleCounts.passing && `${ruleCounts.passing} passing`,
                    ruleCounts.failing && `${ruleCounts.failing} failing`,
                  ]
                    .filter(Boolean)
                    .join(', ')
                : undefined,
            progress: `${ruleScores.length} rules · ${ruleCounts.collecting} still collecting`,
            headline: '',
            headlineValue: null,
          }
        : pending(
            priceRules.isError && riptide.isError && ethHourly.isError && pooled.isError,
          )),
    },
    {
      to: '/crash-bounce',
      name: 'Crash Bounce',
      Icon: CrashIcon,
      what: paperTestSummary('/crash-bounce'),
      ...(crash.data
        ? {
            verdict: crash.data.summary.verdict,
            progress: `${crash.data.summary.trades}/${crash.data.passBar.minTrades} trades · ${crash.data.summary.weeks}/${crash.data.passBar.minWeeks} weeks`,
            progressFraction: Math.min(
              crash.data.summary.trades / crash.data.passBar.minTrades,
              crash.data.summary.weeks / crash.data.passBar.minWeeks,
            ),
            headline: crash.data.summary.trades
              ? `${signedPct(crash.data.summary.meanNetHigh)} a trade`
              : 'No trades yet',
            headlineValue: crash.data.summary.trades
              ? crash.data.summary.meanNetHigh
              : null,
          }
        : pending(crash.isError)),
    },
    {
      to: '/breakout-watch',
      name: 'Breakout Watch',
      Icon: BreakoutIcon,
      what: paperTestSummary('/breakout-watch'),
      ...(breakout.data
        ? {
            verdict: breakout.data.summary.verdict,
            progress: `${breakout.data.summary.trades}/${breakout.data.passBar.minTrades} trades · ${breakout.data.summary.weeks}/${breakout.data.passBar.minWeeks} weeks`,
            progressFraction: Math.min(
              breakout.data.summary.trades / breakout.data.passBar.minTrades,
              breakout.data.summary.weeks / breakout.data.passBar.minWeeks,
            ),
            headline: breakout.data.summary.trades
              ? `${signedPct(breakout.data.summary.meanNetHigh)} a trade`
              : 'No trades yet',
            headlineValue: breakout.data.summary.trades
              ? breakout.data.summary.meanNetHigh
              : null,
          }
        : pending(breakout.isError)),
    },
    {
      to: '/listing-radar',
      name: 'Listing Radar',
      Icon: RadarIcon,
      what: paperTestSummary('/listing-radar'),
      ...(listing.data
        ? {
            verdict: listing.data.summary.verdict,
            progress: `${listing.data.summary.listings}/${listing.data.passBar.minListings} listings · ${listing.data.summary.weeks}/${listing.data.passBar.minWeeks} weeks`,
            progressFraction: Math.min(
              listing.data.summary.listings / listing.data.passBar.minListings,
              listing.data.summary.weeks / listing.data.passBar.minWeeks,
            ),
            headline: listing.data.summary.listings
              ? `${signedPct(listing.data.summary.meanNetHigh)} over 7 days`
              : 'No listings yet',
            headlineValue: listing.data.summary.listings
              ? listing.data.summary.meanNetHigh
              : null,
          }
        : pending(listing.isError)),
    },
    {
      to: '/market-maker',
      name: 'Market Maker',
      Icon: SpreadIcon,
      what: paperTestSummary('/market-maker'),
      ...(maker.data
        ? {
            verdict: maker.data.summary.verdict,
            progress: `${maker.data.summary.rounds}/${maker.data.passBar.minRounds} rounds`,
            progressFraction: maker.data.summary.rounds / maker.data.passBar.minRounds,
            headline: maker.data.summary.rounds
              ? `${formatSignedUsd(maker.data.summary.pessimistic.pnl)} pessimistic`
              : 'No rounds yet',
            headlineValue: maker.data.summary.rounds
              ? maker.data.summary.pessimistic.pnl
              : null,
          }
        : pending(maker.isError)),
    },
    {
      to: '/liquidity-rewards',
      name: 'Liquidity Rewards',
      Icon: RewardIcon,
      what: paperTestSummary('/liquidity-rewards'),
      ...(rewards.data ? liquidityRow(rewards.data.buckets) : pending(rewards.isError)),
    },
    {
      to: '/martingale',
      name: 'Martingale',
      Icon: DoublingIcon,
      what: paperTestSummary('/martingale'),
      ...(martingale.data
        ? martingaleRow(martingale.data.sides.map((s) => s.summary))
        : pending(martingale.isError)),
    },
  ];

  // Stable: tests with the same verdict keep the order above; loading last.
  const ordered = [...rows].sort(
    (a, b) =>
      (a.verdict ? VERDICT_ORDER[a.verdict] : 3) -
      (b.verdict ? VERDICT_ORDER[b.verdict] : 3),
  );

  return (
    <div className={styles.page}>
      <div className={styles.heading}>
        <h1>Paper tests</h1>
      </div>
      <p className={styles.intro}>
        Every rule here was written down before it was tested, and only what happened
        after counts. Decided tests are listed first; tap one for its full record.
      </p>
      <Disclaimer
        summary="Paper only — not a recommendation."
        text="Paper only. Nothing here places an order or is a recommendation to buy, sell or bet; a passing paper test can still lose money for real."
      />
      <BenchmarkCard data={benchmark.data} />
      <ul className={styles.list}>
        {ordered.map((row) => (
          <PaperTestCard key={row.to} row={row} />
        ))}
      </ul>
    </div>
  );
}
